import { Trans, useLingui } from '@lingui/react/macro'
import { Text } from 'designSystem/components/Text'
import {
  DocumentUnavailableBody,
  DocumentUnavailableContainer,
  DocumentUnavailableId,
} from 'features/knowledgeDocumentViewer/components/knowledgeDocumentToolsStyles'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useDocumentConfigAvailability } from 'features/knowledgeDocumentViewer/hooks/useDocumentConfigAvailability'
import { FC, ReactNode } from 'react'
import styled from 'styled-components'

const DebugCard = styled.div`
  width: 100%;
  max-width: 34rem;
  margin-top: 0.75rem;
  padding: 0.75rem 0.9rem;
  text-align: left;
  border: 1px solid ${(props) => props.theme.forecast.border};
  border-radius: 6px;
  background-color: ${(props) => props.theme.forecast.cardBackground};
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`

const DebugSectionLabel = styled.div`
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.8125rem;
  color: ${(props) => props.theme.forecast.defaultText};
`

const DetailKey = styled.span`
  color: ${(props) => props.theme.forecast.secondaryText};
  white-space: nowrap;
`

const DetailValue = styled.code`
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.defaultText};
  word-break: break-all;
  text-align: right;
`

const ConfigList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`

const ConfigRow = styled.li<{ $available: boolean }>`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  font-size: 0.75rem;
  color: ${(props) =>
    props.$available
      ? props.theme.forecast.defaultText
      : props.theme.forecast.secondaryText};
`

const ConfigMark = styled.span<{ $available: boolean }>`
  font-weight: 600;
  color: ${(props) =>
    props.$available
      ? props.theme.forecast.primary
      : props.theme.forecast.secondaryText};
`

const Hint = styled.div`
  font-size: 0.75rem;
  color: ${(props) => props.theme.forecast.secondaryText};
`

const configLabel = (configId: string, status: string | undefined): string => {
  return status ? `${configId} · ${status}` : configId
}

interface DocumentUnavailablePanelProps {
  documentId: string
  siteID: string
  children?: ReactNode
}

/** Document not found in the selected pipeline-config scope. */
export const DocumentUnavailablePanel: FC<DocumentUnavailablePanelProps> = ({
  documentId,
  siteID,
  children,
}) => {
  const { t } = useLingui()
  const { selectedPipelineConfigId, isPreviewMode } = usePipelineConfig()
  const { isGeckoUser } = useKnowledgeDocumentUXContext()
  const { providedConfigId, perConfig, availableConfigs, isLoading } =
    useDocumentConfigAvailability(isGeckoUser ? documentId : undefined, siteID)

  const isAvailableUnderCurrent =
    selectedPipelineConfigId !== undefined &&
    availableConfigs.some(
      (config) => config.configId === selectedPipelineConfigId
    )
  const otherAvailableConfigs = selectedPipelineConfigId
    ? availableConfigs.filter(
        (config) => config.configId !== selectedPipelineConfigId
      )
    : availableConfigs

  return (
    <DocumentUnavailableContainer>
      <Text as='div' size='base' weight='semibold'>
        <Trans>This document isn&apos;t available</Trans>
      </Text>
      <DocumentUnavailableBody>
        <Trans>
          The link may be broken, or the document may not be part of this
          site&apos;s current document set.
        </Trans>
      </DocumentUnavailableBody>
      {children}
      <DocumentUnavailableId>{documentId}</DocumentUnavailableId>

      {isGeckoUser && (
        <DebugCard>
          <DebugSectionLabel>
            <Trans>Diagnostics</Trans>
          </DebugSectionLabel>
          <DetailRow>
            <DetailKey>
              <Trans>Current site config</Trans>
            </DetailKey>
            <DetailValue>
              {selectedPipelineConfigId
                ? isPreviewMode
                  ? t`${selectedPipelineConfigId} · preview`
                  : selectedPipelineConfigId
                : t`none selected`}
            </DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailKey>
              <Trans>This link&apos;s config</Trans>
            </DetailKey>
            <DetailValue>{providedConfigId ?? t`none (bare id)`}</DetailValue>
          </DetailRow>

          <DebugSectionLabel>
            <Trans>Available under</Trans>
          </DebugSectionLabel>
          {isLoading ? (
            <Hint>
              <Trans>Checking this site&apos;s pipeline configs…</Trans>
            </Hint>
          ) : perConfig.length === 0 ? (
            <Hint>
              <Trans>No pipeline configs found for this site.</Trans>
            </Hint>
          ) : (
            <ConfigList>
              {perConfig.map((config) => (
                <ConfigRow
                  key={config.configId}
                  $available={config.isAvailable}>
                  <span>{configLabel(config.configId, config.status)}</span>
                  <ConfigMark $available={config.isAvailable}>
                    {config.isAvailable ? t`✓ available` : t`— not present`}
                  </ConfigMark>
                </ConfigRow>
              ))}
            </ConfigList>
          )}

          {!isLoading &&
            !isAvailableUnderCurrent &&
            otherAvailableConfigs.length > 0 && (
              <Hint>
                <Trans>
                  This document exists only under other pipeline configs. Switch
                  this site&apos;s active config to view it here.
                </Trans>
              </Hint>
            )}
          {!isLoading && availableConfigs.length === 0 && (
            <Hint>
              <Trans>
                This document isn&apos;t present under any of this site&apos;s
                pipeline configs — it may belong to a different site or have
                been re-ingested under a new id.
              </Trans>
            </Hint>
          )}
        </DebugCard>
      )}
    </DocumentUnavailableContainer>
  )
}
